import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Zap, LayoutGrid as GridIcon, KeyboardIcon, HelpCircle } from "lucide-react";
import { listInvestorsGrouped } from "../../lib/api";
import { PhaseBadge } from "./Badges";
import { useInvestorShortcuts } from "../../hooks/useInvestorShortcuts";
import InvestorSpeedCard from "./InvestorSpeedCard";

const GROUP_LABEL = {
  A_ls: "Life Science",
  B_defensive: "Defensive · WAULT 15+",
  C_value_add: "Value-Add",
  Z_sonstige: "Weitere",
};

/**
 * InvestorCardGrid — Welle 5
 * Card-Grid statt Accordion, mit Speed-Modus + Keyboard-Shortcuts + Undo.
 * Default-Gruppierung "sweet_spot".
 */
export default function InvestorCardGrid({
  onOpenDrawer,
  searchQuery = "",
  speedMode = false,
  onToggleSpeed,
  groupBy = "sweet_spot",
}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set(["A_ls", "B_defensive", "C_value_add"]));
  const [focusIdx, setFocusIdx] = useState(-1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showHelp, setShowHelp] = useState(false);
  const containerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listInvestorsGrouped({ by: groupBy, search: searchQuery || undefined });
      setGroups(d.groups || []);
    } catch (e) {
      toast.error("Konnte Gruppen nicht laden");
    } finally { setLoading(false); }
  }, [groupBy, searchQuery]);

  useEffect(() => { load(); }, [load]);

  // Flat list für Keyboard-Nav (nur aus expanded Groups)
  const flatItems = useMemo(() => {
    const items = [];
    for (const g of groups) {
      if (!expanded.has(g.key)) continue;
      // Sortieren: custom_priority (asc: -1 zuerst) → engagement_score desc → last_activity desc
      const sorted = [...(g.items || [])].sort((a, b) => {
        const pa = a.custom_priority || 0, pb = b.custom_priority || 0;
        if (pa !== pb) return pa - pb;
        const ea = a.engagement_score || 0, eb = b.engagement_score || 0;
        if (ea !== eb) return eb - ea;
        return (b.last_activity_at || "").localeCompare(a.last_activity_at || "");
      });
      for (const it of sorted) items.push({ item: it, groupKey: g.key });
    }
    return items;
  }, [groups, expanded]);

  const currentFocused = focusIdx >= 0 && focusIdx < flatItems.length ? flatItems[focusIdx].item : null;

  // Update lokal (nach Quick-Action)
  const applyLocal = useCallback((updated) => {
    setGroups((prev) => prev.map((g) => ({
      ...g,
      items: (g.items || []).map((it) => it.id === updated.id ? { ...it, ...updated } : it),
    })));
  }, []);

  // Keyboard-Shortcut Actions
  const focusNext = useCallback(() => {
    setFocusIdx((i) => Math.min(i + 1, flatItems.length - 1));
  }, [flatItems.length]);
  const focusPrev = useCallback(() => {
    setFocusIdx((i) => Math.max(i - 1, 0));
  }, []);
  const applyToFocused = useCallback(async (action) => {
    if (!currentFocused) return;
    try {
      const { investorQuickAction } = await import("../../lib/api");
      const updated = await investorQuickAction(currentFocused.id, action);
      applyLocal(updated);
      const label = { advance: `→ ${updated.phase}`, regress: `← ${updated.phase}`, disqualify: "→ Abgesagt", pin_up: "gepinnt", pin_down: "unten", unpin: "unpin" }[action];
      toast.success(`${updated.firma}: ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  }, [currentFocused, applyLocal]);
  const openFocused = useCallback(() => {
    if (currentFocused) onOpenDrawer?.(currentFocused.id);
  }, [currentFocused, onOpenDrawer]);

  useInvestorShortcuts({
    enabled: true,
    onFocusNext: focusNext,
    onFocusPrev: focusPrev,
    onAdvance: () => applyToFocused("advance"),
    onRegress: () => applyToFocused("regress"),
    onDisqualify: () => applyToFocused("disqualify"),
    onPinUp: () => applyToFocused("pin_up"),
    onPinDown: () => applyToFocused("pin_down"),
    onOpenDrawer: openFocused,
    onShowHelp: () => setShowHelp((s) => !s),
  });

  // Bei Fokus-Wechsel scrollen
  useEffect(() => {
    if (focusIdx < 0 || focusIdx >= flatItems.length) return;
    const inv = flatItems[focusIdx].item;
    const el = document.querySelector(`[data-testid="speed-card-${inv.id}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [focusIdx, flatItems]);

  const toggleGroup = (key) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div ref={containerRef} data-testid="investor-card-grid" className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => onToggleSpeed?.(!speedMode)}
          data-testid="toggle-speed-mode"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widish font-sans border ${
            speedMode ? "bg-gold text-white border-gold" : "bg-white text-navy border-navy/15 hover:border-navy/30"
          }`}
          title="Kompakte Cards für schnelles Durchgehen"
        >
          <Zap size={12} /> Speed-Modus {speedMode && "AN"}
        </button>
        <button
          onClick={() => setShowHelp(true)}
          data-testid="show-shortcuts-help"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widish font-sans border bg-white text-navy/70 border-navy/15 hover:border-navy/30"
          title="Tastatur-Kürzel"
        >
          <KeyboardIcon size={12} /> Shortcuts (?)
        </button>
        <div className="ml-auto text-xs text-navy/55 font-sans">
          {selectedIds.size > 0 && <span>{selectedIds.size} markiert · </span>}
          Tipp: <kbd className="px-1 py-0.5 border border-navy/15 bg-white text-[10px] font-mono">J</kbd>/<kbd className="px-1 py-0.5 border border-navy/15 bg-white text-[10px] font-mono">K</kbd> Navigation ·
          <kbd className="ml-1 px-1 py-0.5 border border-emerald-500/40 bg-emerald-50 text-[10px] font-mono">→</kbd> qualifizieren ·
          <kbd className="ml-1 px-1 py-0.5 border border-tot/40 bg-tot/10 text-[10px] font-mono">X</kbd> raus
        </div>
      </div>

      {loading && (
        <div className="text-navy/55 font-sans">Lade Investoren …</div>
      )}
      {!loading && groups.length === 0 && (
        <div className="text-navy/55 font-sans">Keine Investoren gefunden.</div>
      )}

      {groups.map((g) => (
        <section
          key={g.key}
          className="border border-navy/12"
          data-testid={`group-${g.key}`}
        >
          <button
            onClick={() => toggleGroup(g.key)}
            data-testid={`group-toggle-${g.key}`}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-navy/[0.03] hover:bg-navy/[0.06] transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-navy font-display text-sm font-semibold">
                {GROUP_LABEL[g.key] || g.key}
              </span>
              <span className="text-xs tabular-nums text-navy/55 font-sans">
                {g.count} {g.count === 1 ? "Investor" : "Investoren"}
              </span>
            </div>
            <span className="text-navy/40 text-sm">{expanded.has(g.key) ? "−" : "+"}</span>
          </button>
          {expanded.has(g.key) && (
            <div className={`p-2 grid gap-2 ${speedMode ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"}`}>
              {(g.items || [])
                .slice()
                .sort((a, b) => {
                  const pa = a.custom_priority || 0, pb = b.custom_priority || 0;
                  if (pa !== pb) return pa - pb;
                  const ea = a.engagement_score || 0, eb = b.engagement_score || 0;
                  if (ea !== eb) return eb - ea;
                  return (b.last_activity_at || "").localeCompare(a.last_activity_at || "");
                })
                .map((inv, idxInGroup) => {
                  const idxFlat = flatItems.findIndex((f) => f.item.id === inv.id);
                  return (
                    <InvestorSpeedCard
                      key={inv.id}
                      investor={inv}
                      searchQuery={searchQuery}
                      speedMode={speedMode}
                      focused={idxFlat === focusIdx}
                      selected={selectedIds.has(inv.id)}
                      onSelect={toggleSelectOne}
                      onLocalUpdate={applyLocal}
                      onOpenDrawer={onOpenDrawer}
                    />
                  );
                })}
              {(g.items || []).length === 0 && (
                <div className="text-navy/50 text-xs font-sans px-2 py-3">— keine Investoren in dieser Gruppe —</div>
              )}
            </div>
          )}
        </section>
      ))}

      {showHelp && (
        <>
          <div onClick={() => setShowHelp(false)} className="fixed inset-0 bg-navy/40 z-50" />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,95vw)] bg-white border border-navy/15 z-[60] p-6" data-testid="shortcuts-help">
            <div className="flex items-center justify-between border-b border-navy/10 pb-3 mb-4">
              <div>
                <div className="pb-eyebrow">Tastatur-Shortcuts</div>
                <h3 className="font-display text-navy font-semibold text-lg mt-1">Speed-Qualifizierung</h3>
              </div>
              <button onClick={() => setShowHelp(false)} className="text-navy/60 hover:text-navy p-2">×</button>
            </div>
            <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2.5 text-sm font-sans text-navy/85">
              <kbd className="px-2 py-0.5 border border-navy/15 bg-navy/[0.03] font-mono text-xs">J / K</kbd>
              <span>Nächster / vorheriger Investor</span>
              <kbd className="px-2 py-0.5 border border-emerald-500/40 bg-emerald-50 font-mono text-xs text-emerald-800">→ / Enter</kbd>
              <span>Qualifizieren — nächste Phase</span>
              <kbd className="px-2 py-0.5 border border-navy/30 bg-navy/[0.03] font-mono text-xs">←</kbd>
              <span>Phase zurück</span>
              <kbd className="px-2 py-0.5 border border-tot/40 bg-tot/10 font-mono text-xs text-tot">X / Delete</kbd>
              <span>Disqualifizieren (→ Abgesagt)</span>
              <kbd className="px-2 py-0.5 border border-navy/15 bg-white font-mono text-xs">Space / D</kbd>
              <span>Detail-Drawer öffnen</span>
              <kbd className="px-2 py-0.5 border border-gold/40 bg-gold/10 font-mono text-xs">Shift+P</kbd>
              <span>Nach oben pinnen</span>
              <kbd className="px-2 py-0.5 border border-navy/15 bg-white font-mono text-xs">Shift+O</kbd>
              <span>Nach unten deprio</span>
              <kbd className="px-2 py-0.5 border border-navy/15 bg-white font-mono text-xs">?</kbd>
              <span>Diese Hilfe ein/aus</span>
            </div>
            <div className="mt-4 pt-3 border-t border-navy/10 text-[11px] text-navy/55 font-sans">
              Jede Aktion kann innerhalb von 60&nbsp;Sekunden per „Rückgängig"-Button im Toast rückgängig gemacht werden.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
